"use client";

import { useState, useEffect, Suspense } from "react";
import { signIn, useSession } from "next-auth/react";
import { useRouter, useSearchParams } from "next/navigation";
import Image from "next/image";

function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const redirect = searchParams.get("redirect") ?? "/home";
  const { status } = useSession();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showAndroidBanner, setShowAndroidBanner] = useState(false);

  // Pokud je uživatel už přihlášený, přesměruj na dashboard
  useEffect(() => {
    if (status === "authenticated") {
      window.location.href = redirect;
    }
  }, [status, redirect]);

  // Vyčisti error parametr z URL (NextAuth ho tam přidává automaticky)
  useEffect(() => {
    const urlError = searchParams.get("error");
    if (urlError) {
      const url = new URL(window.location.href);
      url.searchParams.delete("error");
      url.searchParams.delete("callbackUrl");
      window.history.replaceState({}, "", url.toString());
    }
  }, [searchParams]);

  useEffect(() => {
    const ua = navigator.userAgent;
    if (/Android/i.test(ua) && !ua.includes("SimaIntranetApp")) {
      setShowAndroidBanner(true);
    }
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);

    const result = await signIn("credentials", {
      username,
      password,
      redirect: false,
    });

    setLoading(false);

    if (result?.error) {
      if (result.code === "inactive") {
        setError("Váš účet byl deaktivován. Kontaktujte administrátora.");
      } else {
        setError("Nesprávné uživatelské jméno nebo heslo.");
      }
    } else {
      window.location.href = redirect;
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="w-full max-w-[430px] surface-card p-6 shadow-lg">
        {/* Brand */}
        <div className="flex items-center gap-3 mb-6">
          <Image src="/assets/icon-512.png" alt="Šíma Elektro" width={44} height={44} className="object-contain rounded-2xl" />
          <div>
            <div className="font-bold text-lg tracking-tight">Šíma Elektro</div>
            <div className="text-xs text-muted-foreground">Intranet – Přihlášení</div>
          </div>
        </div>

        {error && (
          <div className="mb-4 px-3 py-2 rounded-xl bg-destructive/10 text-destructive text-sm font-medium">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="username" className="block text-sm font-semibold mb-1.5">
              Uživatel
            </label>
            <input
              id="username"
              type="text"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full rounded-xl border border-black/10 bg-card px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-semibold mb-1.5">
              Heslo
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-xl border border-black/10 bg-card px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full px-4 py-2.5 text-sm font-semibold bg-primary text-primary-foreground rounded-full hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {loading ? "Přihlašuji..." : "Přihlásit se"}
          </button>

          <div className="flex items-center justify-between pt-1">
            <span className="text-xs text-muted-foreground">&copy; {new Date().getFullYear()} Šíma Elektro</span>
            <a href="/register" className="text-xs font-semibold text-lavender hover:opacity-80 transition-opacity">
              Vytvořit účet
            </a>
          </div>
        </form>

        {showAndroidBanner && (
          <div className="mt-5 space-y-2">
            {/* Nová nativní aplikace */}
            <div className="surface-row p-3 bg-lavender/10 border-lavender/20">
              <div className="flex items-center gap-3">
                <Image src="/assets/icon-512.png" alt="App" width={36} height={36} className="rounded-xl bg-card p-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold">Šíma Technik</div>
                  <div className="text-xs text-muted-foreground">Nová mobilní aplikace (offline režim)</div>
                </div>
                <a
                  href="/app/technik.apk"
                  className="shrink-0 px-3 py-1.5 bg-primary text-primary-foreground text-xs font-semibold rounded-full hover:opacity-90 transition-opacity"
                >
                  Stáhnout
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense>
      <LoginForm />
    </Suspense>
  );
}
